import React, { useEffect, useRef, useState } from 'react';
import { TagBoolean } from '../../../components/TagBoolean';
import { ToggleSwitch } from '../../../components/ToggleSwitch';
import { useCollections } from '../../../hooks/useCollections';
import { useExportStatistics, type Export, type ExportStatistics } from '../../../hooks/useExports';
import { TagTypeahead } from '../../../components/TagTypeahead';
import { type ExportFormState } from '../../../types/export';
import { useProjectIdFromRoute } from '../../../hooks/useProjectIdFromRoute';
import { formatBytes, formatNumber } from '../../../utilities/format';

interface Props {
  formState: ExportFormState;
  onChange: (patch: Partial<ExportFormState>) => void;
  exports: Export[];
  onImportTemplate: (exportId: string) => void;
  onValidChange: (valid: boolean) => void;
  onStatsChange?: (stats: ExportStatistics | null) => void;
  acceptTerms: boolean;
  onAcceptTermsChange: (v: boolean) => void;
  warningsAccepted: boolean;
  onWarningsAcceptedChange: (v: boolean) => void;
}

export function Step1Data({ formState, onChange, exports, onImportTemplate, onValidChange, onStatsChange, acceptTerms, onAcceptTermsChange, warningsAccepted, onWarningsAcceptedChange }: Props) {
  const projectId = useProjectIdFromRoute();
  const [collectionSearch, setCollectionSearch] = useState('');
  const [selectAllOpen, setSelectAllOpen] = useState(false);
  const [stats, setStats] = useState<ExportStatistics | null>(null);

  const { data: collections = [] } = useCollections(projectId);
  const statisticsMutation = useExportStatistics(projectId);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced stats fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (formState.collectionIds.length === 0) {
      setStats(null);
      onStatsChange?.(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      statisticsMutation.mutate(
        {
          projectId,
          collectionIds: formState.collectionIds,
          tags: {
            and: formState.tags.and.map(t => t.id),
            or: formState.tags.or.map(t => t.id),
            not: formState.tags.not.map(t => t.id),
          },
          containerExportTag: formState.containerExportTag,
          includeContainerExportTag: formState.includeContainerExportTag,
          includeRedactedResources: formState.includeRedactedResources,
          includeResourcesWithZeroPages: formState.includeResourcesWithZeroPages,
          includeResourcesWithProcessingExceptions: formState.includeResourcesWithProcessingExceptions,
          includeDuplicateResources: formState.includeDuplicateResources,
        },
        { onSuccess: (data) => { setStats(data); onStatsChange?.(data); } },
      );
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formState.collectionIds,
    formState.tags,
    formState.includeContainerExportTag,
    formState.containerExportTag,
    formState.includeRedactedResources,
    formState.includeResourcesWithProcessingExceptions,
    formState.includeResourcesWithZeroPages,
    formState.includeDuplicateResources,
  ]);

  // Emit validity
  useEffect(() => {
    const valid =
      formState.name.trim() !== '' &&
      formState.collectionIds.length > 0 &&
      (stats?.resourceCount ?? 0) > 0 &&
      warningsAccepted &&
      acceptTerms;
    onValidChange(valid);
  }, [formState.name, formState.collectionIds, stats, warningsAccepted, acceptTerms, onValidChange]);

  const filteredCollections = collections
    .filter(c => c.name.toLowerCase().includes(collectionSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  function toggleCollection(id: string, checked: boolean) {
    const ids = checked
      ? [...formState.collectionIds, id]
      : formState.collectionIds.filter(x => x !== id);
    const names = collections.filter(c => ids.includes(c.id)).map(c => c.name);
    onChange({ collectionIds: ids, collectionNames: names });
  }

  function selectAll() {
    onChange({ collectionIds: collections.map(c => c.id), collectionNames: collections.map(c => c.name) });
    setSelectAllOpen(false);
  }

  function selectNone() {
    onChange({ collectionIds: [], collectionNames: [] });
    setSelectAllOpen(false);
  }

  return (
    <div>
      {/* Terms checkbox */}
      <div className="checkbox checkbox-primary">
        <input
          id="acceptTermsAndConditions"
          type="checkbox"
          data-testid="export-wizard-accept-terms-checkbox"
          checked={acceptTerms}
          onChange={e => onAcceptTermsChange(e.target.checked)}
        />
        <label htmlFor="acceptTermsAndConditions">
          I understand this action incurs billable costs and accept all charges incurred by this export.
        </label>
      </div>

      {/* Export name */}
      <div className="row">
        <div className="col-md-12">
          <input
            type="text"
            autoComplete="off"
            placeholder="Export Name"
            className="form-control"
            data-testid="export-wizard-name-input"
            value={formState.name}
            maxLength={255}
            onChange={e => onChange({ name: e.target.value })}
          />
        </div>
      </div>

      {/* Import from previous export */}
      <div className="row" style={{ paddingTop: 15 }}>
        <div className="col-md-6">
          <select
            className="form-control"
            data-testid="export-wizard-import-template-select"
            value=""
            onChange={e => { if (e.target.value) onImportTemplate(e.target.value); }}
          >
            <option value="">-- import settings from previous export --</option>
            {exports.map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Collections + Filtering */}
      <div className="row" style={{ paddingTop: 15 }}>
        <div className="col-md-6">
          <h5>Collections</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', height: 377, overflow: 'auto' }}>
            <div className="form-group">
              <div className="input-group">
                <div className="input-group-btn">
                  <div className={`btn-group${selectAllOpen ? ' open' : ''}`}>
                    <button
                      type="button"
                      className="btn btn-default"
                      data-testid="export-wizard-select-collections-btn"
                      onClick={() => setSelectAllOpen(v => !v)}
                    >
                      <span className="far fa-check-square fa-fw" />
                      <span className="caret" />
                    </button>
                    {selectAllOpen && (
                      <ul className="dropdown-menu" data-testid="export-wizard-select-collections-menu">
                        <li><a onClick={selectAll} style={{ cursor: 'pointer' }} data-testid="export-wizard-select-all-menu-item">All</a></li>
                        <li><a onClick={selectNone} style={{ cursor: 'pointer' }} data-testid="export-wizard-select-none-menu-item">None</a></li>
                      </ul>
                    )}
                  </div>
                </div>
                <input
                  type="text"
                  className="form-control"
                  style={{ height: 'auto' }}
                  placeholder="Filter Collections"
                  data-testid="export-wizard-collection-filter-input"
                  value={collectionSearch}
                  onChange={e => setCollectionSearch(e.target.value)}
                />
              </div>
            </div>
            <ul className="list-unstyled" data-testid="export-wizard-collection-list">
              {filteredCollections.map(c => (
                <li key={c.id}>
                  <div className="checkbox checkbox-primary">
                    <input
                      id={`col-${c.id}`}
                      type="checkbox"
                      data-testid="export-wizard-collection-checkbox"
                      checked={formState.collectionIds.includes(c.id)}
                      onChange={e => toggleCollection(c.id, e.target.checked)}
                    />
                    <label htmlFor={`col-${c.id}`}>{c.name}</label>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="col-md-6">
          <h5>Filtering</h5>
          <TagBoolean
            projectId={projectId}
            value={formState.tags}
            onChange={tags => onChange({ tags })}
            testId="export-wizard-tags-panel"
          />
        </div>
      </div>

      {/* File Types */}
      <div className="row">
        <div className="col-md-12">
          <h5>File Types</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff' }}>
            <div className="checkbox checkbox-primary">
              <input id="exportRegularFiles" type="checkbox" checked disabled readOnly />
              <label htmlFor="exportRegularFiles">Regular Files</label>
            </div>
            <div className="clearfix">
              <div style={{ float: 'left', width: 190 }}>
                <div className="checkbox checkbox-primary">
                  <input
                    id="containerExportTag"
                    type="checkbox"
                    data-testid="export-wizard-container-tag-checkbox"
                    checked={formState.includeContainerExportTag}
                    onChange={e => onChange({ includeContainerExportTag: e.target.checked })}
                  />
                  <label htmlFor="containerExportTag">Containers Tagged With</label>
                </div>
              </div>
              <div style={{ float: 'left' }}>
                <div className="form-group">
                  {formState.includeContainerExportTag ? (
                    <TagTypeahead
                      projectId={projectId}
                      selected={formState.containerExportTag ? [{ ...formState.containerExportTag, color: '' }] : []}
                      onChange={tags => onChange({ containerExportTag: tags[0] ? { id: tags[0].id, name: tags[0].name } : null })}
                      placeholder="Tag name..."
                      testId="export-wizard-container-tag-select"
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      disabled
                      value={formState.containerExportTag?.name ?? ''}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="row">
        <div className="col-md-12">
          <h5>Summary</h5>
          <div className="pull-right" style={{ marginTop: -30 }}>
            <ul className="nav nav-pills" role="tablist" data-testid="export-wizard-stats-list">
              <li role="presentation">
                <a href="javascript:void(0)">
                  Resources <span className="badge">{formatNumber(stats?.resourceCount ?? 0)}</span>
                </a>
              </li>
              <li role="presentation">
                <a href="javascript:void(0)">
                  Pages <span className="badge">{formatNumber(stats?.pageCount ?? 0)}</span>
                </a>
              </li>
              <li role="presentation">
                <a href="javascript:void(0)">
                  Size <span className="badge">{formatBytes(stats?.size ?? 0)}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Include/Exclude toggles */}
      <div className="row"><div className="col-md-12">&nbsp;</div></div>
      <div className="row">
        <div className="col-md-6">
          <ToggleSwitch
            checked={formState.includeResourcesWithProcessingExceptions}
            onChange={v => onChange({ includeResourcesWithProcessingExceptions: v })}
            testId="export-wizard-exceptions-toggle"
            onLabel="Include"
            offLabel="Exclude"
            size="small"
          />
          {' '}
          <span className="badge">{formatNumber(stats?.exceptions ?? 0)}</span> Processing Exceptions
        </div>
        <div className="col-md-6">
          <ToggleSwitch
            checked={formState.includeDuplicateResources}
            onChange={v => onChange({ includeDuplicateResources: v })}
            testId="export-wizard-duplicates-toggle"
            onLabel="Include"
            offLabel="Exclude"
            size="small"
          />
          {' '}
          <span className="badge">{formatNumber(stats?.duplicates ?? 0)}</span> Duplicates
        </div>
      </div>
      <div className="row"><div className="col-md-12">&nbsp;</div></div>
      <div className="row">
        <div className="col-md-6">
          <ToggleSwitch
            checked={formState.includeResourcesWithZeroPages}
            onChange={v => onChange({ includeResourcesWithZeroPages: v })}
            testId="export-wizard-zero-pages-toggle"
            onLabel="Include"
            offLabel="Exclude"
            size="small"
          />
          {' '}
          <span className="badge">{formatNumber(stats?.zeroPages ?? 0)}</span> No Pages
        </div>
        <div className="col-md-6">
          <ToggleSwitch
            checked={formState.includeRedactedResources}
            onChange={v => onChange({ includeRedactedResources: v })}
            testId="export-wizard-redacted-toggle"
            onLabel="Include"
            offLabel="Exclude"
            size="small"
          />
          {' '}
          <span className="badge">{formatNumber(stats?.redacted ?? 0)}</span> Redacted
        </div>
      </div>

      {/* Warnings accepted */}
      <div className="row"><div className="col-md-12">&nbsp;</div></div>
      <div className="row">
        <div className="col-md-12">
          <div className="checkbox checkbox-primary pull-right">
            <input
              id="acceptWarnings"
              type="checkbox"
              data-testid="export-wizard-accept-warnings-checkbox"
              checked={warningsAccepted}
              onChange={e => onWarningsAcceptedChange(e.target.checked)}
            />
            <label htmlFor="acceptWarnings">I have reviewed and accept the above selections</label>
          </div>
        </div>
      </div>
    </div>
  );
}
